import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from './utils';
import { Menu, X, Instagram, Facebook, Construction } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import ScrollToTop from './components/ScrollToTop';
import { useLanguage } from '@/i18n/LanguageProvider';

export default function Layout({ children, currentPageName }) {
  const { t, language } = useLanguage();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [user, setUser] = React.useState(null);
  const [checkingAuth, setCheckingAuth] = React.useState(true);

  const { data: settings = [] } = useQuery({
    queryKey: ['site-settings'],
    queryFn: () => base44.entities.SiteSettings.list(),
    initialData: []
  });

  const maintenanceSetting = settings.find(s => s.key === 'maintenance_mode');
  const isMaintenanceMode = maintenanceSetting?.value || false;

  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
      } catch (error) {
        setUser(null);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [currentPageName]);

  React.useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navItems = [
  { name: 'Home', page: 'Home' },
  { name: 'Progetti', page: 'Projects' },
  { name: 'Visualizzatore 3D', page: 'InteractiveView' },
  { name: 'Studio', page: 'Studio' },
  { name: 'Contatti', page: 'Contact' }];


  const isHomePage = currentPageName === 'Home';
  const headerBg = menuOpen || !isHomePage || isScrolled ? 'bg-white/95 backdrop-blur-sm' : 'bg-transparent';
  const textColor = menuOpen || !isHomePage || isScrolled ? 'text-[#2d2d2d]' : 'text-white';

  // Show maintenance page if maintenance mode is on and user is not admin (except on Gestione page)
  if (!checkingAuth && isMaintenanceMode && user?.role !== 'admin' && currentPageName !== 'Gestione') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f8f8] to-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Construction className="w-20 h-20 mx-auto mb-6 text-[#74675a]" />
          <h1 className="text-4xl font-serif font-light text-[#2d2d2d] mb-4">
            {t('Sito in Costruzione')}
          </h1>
          <p className="text-[#5f5f5f] text-lg leading-relaxed">
            {t('Stiamo lavorando per migliorare la tua esperienza. Torna presto a trovarci!')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <ScrollToTop />
      <Helmet>
        <html lang={language} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}>

        <nav className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex h-16 items-center justify-between md:h-24">
            {/* Logo */}
            <Link
              to={createPageUrl('Home')}
              className={`flex items-center gap-3 ${textColor} transition-colors duration-300`}>

              <img
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/696b8a6ae79eec44c999be35/51939c246_image-removebg-preview.png"
                alt="Bellomonte & Pensabene"
                width="276"
                height="48"
                decoding="async"
                className="h-8 w-auto md:h-12" />

              <div className="hidden sm:block">
                <div className="text-xs tracking-widest-custom uppercase font-light">Bellomonte & Pensabene</div>
                <div className="text-[10px] tracking-widest-custom uppercase font-light opacity-70">{t('Studio di Architettura')}</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-12">
              {navItems.map((item) =>
              <Link
                key={item.page}
                to={createPageUrl(item.page)}
                className={`text-xs tracking-widest-custom uppercase font-light ${textColor} 
                    hover:opacity-60 transition-all duration-300 relative group`}>

                  {t(item.name)}
                  <span className={`absolute -bottom-1 left-0 h-px bg-current transition-all duration-300
                    ${currentPageName === item.page ? 'w-full' : 'w-0 group-hover:w-full'}`} />

                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t('Chiudi menu') : t('Apri menu')}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              className={`flex h-11 w-11 items-center justify-center md:hidden ${textColor} transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current`}>
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen &&
        <motion.div
          id="mobile-navigation"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed inset-0 z-40 bg-white pt-16 md:hidden">
            <nav aria-label="Navigazione mobile" className="flex h-full flex-col items-start justify-center gap-2 px-8 pb-16">
              {navItems.map((item, index) =>
                <motion.div key={item.page} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: index * 0.04 }}>
                  <Link
                    to={createPageUrl(item.page)}
                    aria-current={currentPageName === item.page ? 'page' : undefined}
                    className={`flex min-h-14 items-center font-serif text-[32px] ${currentPageName === item.page ? 'text-[#74675a]' : 'text-[#2d2d2d]'}`}>
                    {t(item.name)}
                  </Link>
                </motion.div>
              )}
            </nav>
          </motion.div>
        }
      </AnimatePresence>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#f8f8f8] py-10 md:py-24">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-4 md:gap-8">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/696b8a6ae79eec44c999be35/51939c246_image-removebg-preview.png"
                  alt="Bellomonte & Pensabene"
                  width="276"
                  height="48"
                  loading="lazy"
                  decoding="async"
                  className="h-12 w-auto" />

                <div>
                  <div className="text-xs tracking-widest-custom uppercase font-light">Bellomonte & Pensabene</div>
                  <div className="text-[10px] tracking-widest-custom uppercase font-light opacity-70">{t('Studio di Architettura e Interior Design')}</div>
                </div>
              </div>
              


            </div>

            <div>
              <p className="text-xs tracking-widest-custom uppercase mb-6">Navigation</p>
              <div className="flex flex-col gap-3">
                {navItems.map((item) =>
                <Link
                  key={item.page}
                  to={createPageUrl(item.page)}
                  className="text-sm text-[#5f5f5f] hover:text-[#2d2d2d] transition-colors">

                    {t(item.name)}
                  </Link>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs tracking-widest-custom uppercase mb-6">{t('Contatti')}</p>
              <div className="flex flex-col items-start gap-1 text-sm text-[#666666]">
                <a href="mailto:rosariopensabenearc@yahoo.it" className="flex min-h-11 items-center hover:text-[#2d2d2d]">rosariopensabenearc@yahoo.it</a>
                <a href="tel:+393493810464" className="flex min-h-11 items-center hover:text-[#2d2d2d]">+39 349 381 0464</a>
                <a href="https://www.google.com/maps/search/?api=1&query=Viale+Regione+Siciliana+Nord+Ovest+2132+Palermo" target="_blank" rel="noopener noreferrer" className="pt-2 leading-relaxed hover:text-[#2d2d2d]">Viale Regione Siciliana<br />Nord Ovest 2132<br />Palermo 90135</a>
              </div>
            </div>

            <div>
              <p className="text-xs tracking-widest-custom uppercase mb-6">{t('Seguici')}</p>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/bellomontepensabene_architects/"
                  aria-label="Instagram — Bellomonte & Pensabene"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[#e5e5e5] flex items-center justify-center 
                    text-[#5f5f5f] hover:text-[#2d2d2d] hover:border-[#2d2d2d] transition-all duration-300">


                  <Instagram size={18} />
                </a>
                <a
                  href="https://www.facebook.com/bellomontepensabene"
                  aria-label="Facebook — Bellomonte & Pensabene"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[#e5e5e5] flex items-center justify-center 
                    text-[#5f5f5f] hover:text-[#2d2d2d] hover:border-[#2d2d2d] transition-all duration-300">


                  <Facebook size={18} />
                </a>
                <a
                  href="https://www.tiktok.com/@bellomonte_pensabene"
                  aria-label="TikTok — Bellomonte & Pensabene"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[#e5e5e5] flex items-center justify-center 
                    text-[#5f5f5f] hover:text-[#2d2d2d] hover:border-[#2d2d2d] transition-all duration-300">


                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
              </div>
            </div>
            </div>

          <div className="mt-10 border-t border-[#e5e5e5] pt-6 md:mt-16 md:pt-8">
            <p className="text-xs text-[#5f5f5f] text-center">
              © {new Date().getFullYear()} Bellomonte & Pensabene {t('Studio di Architettura')}. {t('Tutti i diritti riservati.')}
            </p>
          </div>
        </div>
      </footer>
    </div>);

}